import { type FunctionComponent, useState } from 'react'
import React from 'react'
import styled from '@emotion/styled'
import {
  typography,
  ItemsList,
  Flex,
  TDS_DEFAULT_COLORS,
  spacing,
} from '@retailer-platform/shared-components/src/tds'
import { NotificationScopeTypeEnum, type NotificationsNotificationChannel } from '../../../../api'
import { ToggleType, COLUMN_WIDTHS } from '../types'
import { useUnsubscribeParams } from '../hooks/useUnsubscribeParams'
import { NotificationToggle } from './NotificationToggle'

const ColumnContent = styled.div({
  ...typography.bodyRegular,
})

const BoldColumnContent = styled(ColumnContent)({
  ...typography.bodyEmphasized,
})

interface NotificationSettingRowProps {
  channel: NotificationsNotificationChannel
  partnerName?: string
  selectedBanner?: string
  bannerName?: string
  isDisabled?: boolean
  showInApp?: boolean
  scopeId?: string
}

export const NotificationSettingRow: FunctionComponent<NotificationSettingRowProps> = ({
  channel,
  partnerName,
  selectedBanner,
  bannerName,
  isDisabled,
  scopeId,
  showInApp = true,
}) => {
  const [channelState, setChannelState] = useState(channel)
  const unsubscribeParams = useUnsubscribeParams()
  const [highlightedChannel, setHighlightedChannel] = useState('')

  const handleToggleSuccess = (type: ToggleType) => {
    setChannelState(prev => ({
      ...prev,
      isEmailSubscribed:
        type === ToggleType.Email ? !prev.isEmailSubscribed : prev.isEmailSubscribed,
      isInAppSubscribed:
        type === ToggleType.InApp ? !prev.isInAppSubscribed : prev.isInAppSubscribed,
    }))
  }
  React.useEffect(() => {
    if (unsubscribeParams?.notificationChannelId === channel.id) {
      setHighlightedChannel(unsubscribeParams.notificationChannelId as string)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <ItemsList.Section
      data-testid={`notification-setting-row-${channel.id}`}
      css={{
        backgroundColor:
          highlightedChannel === channel.id
            ? TDS_DEFAULT_COLORS.colors.systemAlertLight
            : 'transparent',
      }}
    >
      <Flex.Row gap={spacing.s8} alignItems="center">
        <Flex.Column css={{ width: COLUMN_WIDTHS.EVENT_NAME }}>
          <BoldColumnContent>{channelState.name}</BoldColumnContent>
        </Flex.Column>
        <Flex.Column css={{ width: COLUMN_WIDTHS.DESCRIPTION }}>
          <ColumnContent>{channelState.description || ''}</ColumnContent>
        </Flex.Column>
        <Flex.Column css={{ width: COLUMN_WIDTHS.PARTNER }}>
          {partnerName && <BoldColumnContent>{partnerName}</BoldColumnContent>}
        </Flex.Column>
        <Flex.Column css={{ width: COLUMN_WIDTHS.EMAIL }}>
          <NotificationToggle
            type={ToggleType.Email}
            isSubscribed={!!channelState.isEmailSubscribed}
            channelName={channelState.name}
            channelSlug={channelState.slug}
            channelId={channel.id}
            scopeId={scopeId}
            bannerName={bannerName}
            onToggleSuccess={() => handleToggleSuccess(ToggleType.Email)}
            isDisabled={
              isDisabled ||
              (channel.scopeType === NotificationScopeTypeEnum.Banner &&
                selectedBanner !== undefined &&
                !selectedBanner)
            }
          />
        </Flex.Column>
        {showInApp && (
          <Flex.Column css={{ width: COLUMN_WIDTHS.IN_APP }}>
            <NotificationToggle
              type={ToggleType.InApp}
              isSubscribed={!!channelState.isInAppSubscribed}
              channelName={channelState.name}
              channelSlug={channelState.slug}
              channelId={channel.id}
              scopeId={scopeId}
              bannerName={bannerName}
              onToggleSuccess={() => handleToggleSuccess(ToggleType.InApp)}
              isDisabled={
                isDisabled ||
                (channel.scopeType === NotificationScopeTypeEnum.Banner &&
                  selectedBanner !== undefined &&
                  !selectedBanner)
              }
            />
          </Flex.Column>
        )}
        <Flex.Column css={{ width: COLUMN_WIDTHS.BANNER }}>
          {selectedBanner && <BoldColumnContent>{bannerName}</BoldColumnContent>}
          {!selectedBanner && <div data-testid="banner-column" />}
        </Flex.Column>
      </Flex.Row>
    </ItemsList.Section>
  )
}
